import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ShieldAlertIcon } from 'lucide-react';
import { Button } from './ui/Button';
interface FinalCTAProps {
  bump1Selected: boolean;
  setBump1Selected: (val: boolean) => void;
  bump2Selected: boolean;
  setBump2Selected: (val: boolean) => void;
  displayTotal: string;
  checkoutUrl: string;
  onBeforeCheckout?: () => boolean;
}
export function FinalCTA({
  bump1Selected,
  setBump1Selected,
  bump2Selected,
  setBump2Selected,
  displayTotal,
  checkoutUrl,
  onBeforeCheckout
}: FinalCTAProps) {
  return <section id="final-offer" className="bg-navy py-24 px-6 text-center" data-id="element-173">
      <div className="max-w-3xl mx-auto" data-id="element-174">
        <div className="text-lg md:text-xl text-white font-body leading-relaxed space-y-6 text-left mb-12 bg-navy-light/30 p-8 rounded-xl border border-white/10" data-id="element-175">
          <p data-id="element-176">
            The mistakes heirs make in the first 90 days aren't small ones.
            Wrong title transfer: $8,000–$15,000 in penalties. Missed probate
            deadlines: months of added delays. A conversation with siblings that
            goes wrong: years of legal fees.
          </p>
          <p data-id="element-177">
            None of those mistakes announce themselves in advance. They happen
            quietly — while you're still "figuring it out."
          </p>
          <p className="font-bold text-white text-xl" data-id="element-178">
            This kit was designed to be read tonight, before any of those
            windows close.
          </p>
        </div>

        <div className="flex flex-col items-center gap-6" data-id="element-179">
          <Button href={checkoutUrl} trackEvent="InitiateCheckout" className="text-xl px-8 py-5 w-full md:w-auto" onBeforeNavigate={onBeforeCheckout} data-id="element-180">
            GET THE SURVIVAL KIT + ALL 4 BONUSES — {displayTotal}
          </Button>

          <p className="text-lg text-white font-body leading-relaxed text-center max-w-2xl mt-4" data-id="element-181">
            The house isn't waiting. Your siblings aren't waiting. The tax
            calendar isn't waiting. You have a plan available right now for{' '}
            {displayTotal}. The only question is whether you use it tonight or
            after something goes wrong.
          </p>

          {/* Order Bumps */}
          <div className="w-full space-y-6 mt-8" data-id="element-182">
            {/* Order Bump #1 */}
            <motion.div animate={{
            boxShadow: bump1Selected ? '0 0 0 2px #b8860b' : ['0 0 0 0px rgba(184,134,11,0)', '0 0 20px 4px rgba(184,134,11,0.35)', '0 0 0 0px rgba(184,134,11,0)']
          }} transition={bump1Selected ? {
            duration: 0.2
          } : {
            duration: 2.5,
            repeat: Infinity,
            repeatDelay: 1
          }} className={`rounded-xl overflow-hidden cursor-pointer transition-colors ${bump1Selected ? 'bg-navy-light/60 border-2 border-gold' : 'bg-navy-light/40 border border-white/15'}`} onClick={() => setBump1Selected(!bump1Selected)} data-id="element-183">
              <div className="bg-amber-600 text-white py-2 px-4 flex items-center gap-2 justify-center" data-id="element-184">
                <AlertTriangleIcon className="w-5 h-5" data-id="element-185" />
                <span className="font-bold text-sm md:text-base uppercase tracking-wide" data-id="element-186">
                  Wait! Don't Dig Through a Dead Relative's Files Alone.
                </span>
              </div>
              <div className="p-5 md:p-6 flex flex-col md:flex-row gap-5 items-start text-left" data-id="element-187">
                <img src="https://cdn.magicpatterns.com/uploads/han6YxcQyCwtddeWkfjd84/Oder_Bump_1_The_Shoebox.png" alt="The Executor's Shoebox Sort" className="w-full md:w-36 rounded-lg object-cover shrink-0" data-id="element-188" />
                <div className="flex-1" data-id="element-189">
                  <p className="text-white/90 font-body text-sm md:text-base leading-relaxed mb-4" data-id="element-190">
                    Most heirs waste 40+ hours digging through "junk" mail and
                    old desks, only to miss the ONE document the bank actually
                    needs.
                  </p>
                  <p className="text-white/90 font-body text-sm md:text-base leading-relaxed mb-4" data-id="element-191">
                    Add{' '}
                    <strong className="text-white" data-id="element-192">
                      The Executor's Shoebox Sort
                    </strong>{' '}
                    to your order now. This tactical guide shows you how to
                    organize 20+ years of a parent's records in a single
                    afternoon. You'll get the "5-Bin System" used by
                    professional estate managers to find deeds, hidden insurance
                    policies, and account passwords without the mental
                    breakdown.
                  </p>
                  <p className="text-gold font-bold text-sm md:text-base" data-id="element-193">
                    If you don't find the mortgage statement this week, you
                    can't stop the foreclosure clock. Get organized tonight for
                    just $9.87.
                  </p>
                  <label className="flex items-center gap-3 mt-4 cursor-pointer" data-id="element-194">
                    <input type="checkbox" checked={bump1Selected} onChange={() => setBump1Selected(!bump1Selected)} className="w-5 h-5 accent-gold rounded cursor-pointer" data-id="element-195" />
                    <span className="text-white font-bold text-sm md:text-base" data-id="element-196">
                      Yes! Add this to my order for $9.87
                    </span>
                  </label>
                </div>
              </div>
            </motion.div>

            {/* Order Bump #2 */}
            <motion.div animate={{
            boxShadow: bump2Selected ? '0 0 0 2px #b8860b' : ['0 0 0 0px rgba(184,134,11,0)', '0 0 20px 4px rgba(184,134,11,0.35)', '0 0 0 0px rgba(184,134,11,0)']
          }} transition={bump2Selected ? {
            duration: 0.2
          } : {
            duration: 2.5,
            repeat: Infinity,
            repeatDelay: 1.5
          }} className={`rounded-xl overflow-hidden cursor-pointer transition-colors ${bump2Selected ? 'bg-navy-light/60 border-2 border-gold' : 'bg-navy-light/40 border border-white/15'}`} onClick={() => setBump2Selected(!bump2Selected)} data-id="element-197">
              <div className="bg-amber-600 text-white py-2 px-4 flex items-center gap-2 justify-center" data-id="element-198">
                <ShieldAlertIcon className="w-5 h-5" data-id="element-199" />
                <span className="font-bold text-sm md:text-base uppercase tracking-wide" data-id="element-200">
                  Stop the Family Arguments Before They Start.
                </span>
              </div>
              <div className="p-5 md:p-6 flex flex-col md:flex-row gap-5 items-start text-left" data-id="element-201">
                <img src="https://cdn.magicpatterns.com/uploads/ajZvVkoroUwGDdDMp6Kjgh/Order_Bump_2_The_Family_Meeting.png" alt="The Family Meeting Survival Script" className="w-full md:w-36 rounded-lg object-cover shrink-0" data-id="element-202" />
                <div className="flex-1" data-id="element-203">
                  <p className="text-white/90 font-body text-sm md:text-base leading-relaxed mb-4" data-id="element-204">
                    Nothing destroys a family faster than an inherited house.
                    One sibling wants to keep it, one wants to sell it, and one
                    is already trying to move in.
                  </p>
                  <p className="text-white/90 font-body text-sm md:text-base leading-relaxed mb-4" data-id="element-205">
                    Add{' '}
                    <strong className="text-white" data-id="element-206">
                      The Family Meeting Survival Script
                    </strong>{' '}
                    to your order. You'll get the exact, word-for-word scripts
                    to lead your first family meeting with authority. Learn how
                    to present the "Holding Costs" so everyone sees the math,
                    and use our "Conflict De-escalators" to shut down old
                    grudges before they turn into a lawsuit.
                  </p>
                  <p className="text-gold font-bold text-sm md:text-base" data-id="element-207">
                    Be the leader your family needs — not the one they fight
                    with. Secure the peace and the inheritance for just $9.87.
                  </p>
                  <label className="flex items-center gap-3 mt-4 cursor-pointer" data-id="element-208">
                    <input type="checkbox" checked={bump2Selected} onChange={() => setBump2Selected(!bump2Selected)} className="w-5 h-5 accent-gold rounded cursor-pointer" data-id="element-209" />
                    <span className="text-white font-bold text-sm md:text-base" data-id="element-210">
                      Yes! Add this to my order for $9.87
                    </span>
                  </label>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="flex flex-col items-center gap-2 mt-6" data-id="element-211">
            <p className="text-sm text-white font-body flex flex-wrap justify-center gap-2" data-id="element-212">
              <span data-id="element-213">Instant Download</span>
              <span className="hidden sm:inline" data-id="element-214">|</span>
              <span data-id="element-215">Works on Phone, Tablet & Computer</span>
              <span className="hidden sm:inline" data-id="element-216">|</span>
              <span data-id="element-217">30-Day Money-Back Guarantee</span>
            </p>
            <p className="text-sm text-gold font-body text-center mt-2" data-id="element-218">
              Limited-time offer. Bonuses available while this page is live.
            </p>
          </div>
        </div>
      </div>
    </section>;
}